import { MediaAssetInput } from '../../components/settings/MediaAssetPicker';
import { mediaUrl } from '../../components/media/MediaAssetLibrary';
import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import NotificationSnackbar from '../../components/ui/NotificationSnackbar'
import { useStorefrontCms } from '../../services/fusionBoxCmsService';

const BACKEND = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'

const emptyBlog = {
  title: '', slug: '', excerpt: '', body_html: '', featured_image_url: '', author_name: '',
  category: '', tags: '', seo_title: '', seo_description: '', is_featured: false, status: 'draft', published_at: '',
}

const slugify = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/['"]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')

const toLocalDatetime = (value) => value ? String(value).replace(' ', 'T').slice(0, 16) : ''

export default function BlogsEditor() {
  const { fetchBlogs, createBlog, updateBlog, deleteBlog } = useStorefrontCms();
  const [blogs, setBlogs] = useState([])
  const [selected, setSelected] = useState(null)
  const [form, setForm] = useState(emptyBlog)
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' })
  const fileRef = useRef(null)

  const notify = (message, severity = 'success') => setNotification({ open: true, message, severity })

  const load = async () => {
    setLoading(true)
    try {
      const rows = await fetchBlogs()
      setBlogs(Array.isArray(rows) ? rows : [])
      if (!selected && rows?.[0]) selectBlog(rows[0])
    } catch {
      notify('Failed to load blogs. Run the new migration first.', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const selectBlog = (blog) => {
    setSelected(blog)
    setForm({
      title: blog.title || '',
      slug: blog.slug || '',
      excerpt: blog.excerpt || '',
      body_html: blog.body_html || '',
      featured_image_url: blog.featured_image_url || '',
      author_name: blog.author_name || '',
      category: blog.category || '',
      tags: blog.tags || '',
      seo_title: blog.seo_title || '',
      seo_description: blog.seo_description || '',
      is_featured: Boolean(blog.is_featured),
      status: blog.status || 'draft',
      published_at: toLocalDatetime(blog.published_at),
    })
    setImageFile(null)
    setImagePreview(blog.featured_image_url ? (blog.featured_image_url.startsWith('http') ? blog.featured_image_url : `${BACKEND}${blog.featured_image_url}`) : '')
  }

  const newBlog = () => {
    setSelected(null)
    setForm(emptyBlog)
    setImageFile(null)
    setImagePreview('')
  }

  const patch = (key, value) => setForm(f => ({ ...f, [key]: value }))

  const chooseImage = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    setImageFile(f)
    setImagePreview(URL.createObjectURL(f))
  }

  const toFormData = () => {
    const fd = new FormData()
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'is_featured') fd.append(k, v ? 1 : 0)
      else if (v !== undefined && v !== null) fd.append(k, v)
    })
    if (imageFile) fd.append('image', imageFile)
    return fd
  }

  const save = async () => {
    if (!form.title.trim()) return notify('Blog title is required', 'error')
    setSaving(true)
    try {
      const fd = toFormData()
      const row = selected ? await updateBlog(selected.id, fd) : await createBlog(fd)
      await load()
      selectBlog(row)
      notify(selected ? 'Blog updated' : 'Blog created')
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to save blog', 'error')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!selected || !window.confirm(`Delete blog "${selected.title}"?`)) return
    try {
      await deleteBlog(selected.id)
      setSelected(null)
      setForm(emptyBlog)
      await load()
      notify('Blog deleted')
    } catch {
      notify('Failed to delete blog', 'error')
    }
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>

  return (
    <Box sx={{ p: 3, background: '#f8fafc', minHeight: 'calc(100vh - 64px)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Blogs CMS</Typography>
          <Typography variant="body2" color="text.secondary">Create blog posts for the CMS blog list module.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={newBlog}>New Blog</Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardHeader title="All Blogs" titleTypographyProps={{ fontWeight: 800 }} />
            <CardContent sx={{ pt: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {blogs.map(blog => (
                <Button key={blog.id} variant={selected?.id === blog.id ? 'contained' : 'outlined'} onClick={() => selectBlog(blog)} sx={{ justifyContent: 'space-between', textTransform: 'none' }}>
                  <span>{blog.title}</span>
                  <Chip size="small" label={blog.status} color={blog.status === 'published' ? 'success' : 'default'} />
                </Button>
              ))}
              {!blogs.length && <Typography variant="body2" color="text.secondary">No blogs yet.</Typography>}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3 }}>
            <CardHeader
              title={selected ? 'Edit Blog' : 'New Blog'}
              titleTypographyProps={{ fontWeight: 800 }}
              action={(
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
                  {selected && <IconButton color="error" onClick={remove}><DeleteIcon /></IconButton>}
                </Box>
              )}
            />
            <CardContent sx={{ pt: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}><TextField label="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value, slug: f.slug || slugify(e.target.value) }))} fullWidth size="small" /></Grid>
                <Grid item xs={12} md={4}><TextField label="Slug" value={form.slug} onChange={e => patch('slug', slugify(e.target.value))} fullWidth size="small" /></Grid>
                <Grid item xs={12}><TextField label="Excerpt" value={form.excerpt} onChange={e => patch('excerpt', e.target.value)} fullWidth size="small" multiline rows={2} /></Grid>
                <Grid item xs={12}><TextField label="Body HTML" value={form.body_html} onChange={e => patch('body_html', e.target.value)} fullWidth size="small" multiline rows={10} helperText="Use simple HTML. This will render on blog detail pages later if needed." /></Grid>
                <Grid item xs={12} md={6}><TextField label="Author" value={form.author_name} onChange={e => patch('author_name', e.target.value)} fullWidth size="small" /></Grid>
                <Grid item xs={12} md={6}><TextField label="Category" value={form.category} onChange={e => patch('category', e.target.value)} fullWidth size="small" /></Grid>
                <Grid item xs={12}><TextField label="Tags" value={form.tags} onChange={e => patch('tags', e.target.value)} fullWidth size="small" helperText="Comma separated" /></Grid>
                <Grid item xs={12} md={6}>
                  <TextField select label="Status" value={form.status} onChange={e => patch('status', e.target.value)} fullWidth size="small">
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}><TextField label="Published At" type="datetime-local" value={form.published_at} onChange={e => patch('published_at', e.target.value)} fullWidth size="small" InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12}><FormControlLabel control={<Switch checked={form.is_featured} onChange={e => patch('is_featured', e.target.checked)} />} label="Featured blog" /></Grid>
                <Grid item xs={12}>
                  {imagePreview && <img src={imagePreview} alt="preview" style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 12, marginBottom: 10 }} />}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={() => fileRef.current?.click()}>{imagePreview ? 'Change Featured Image' : 'Choose Featured Image'}</Button>
                    <TextField label="Or Image URL" value={form.featured_image_url} onChange={e => { patch('featured_image_url', e.target.value); setImagePreview(e.target.value) }} fullWidth size="small" />
                    <MediaAssetInput ref={fileRef} onSelect={asset => { setImageFile(null); patch('featured_image_url', asset.url); setImagePreview(mediaUrl(asset.url)); }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}><TextField label="SEO Title" value={form.seo_title} onChange={e => patch('seo_title', e.target.value)} fullWidth size="small" /></Grid>
                <Grid item xs={12} md={6}><TextField label="SEO Description" value={form.seo_description} onChange={e => patch('seo_description', e.target.value)} fullWidth size="small" /></Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <NotificationSnackbar open={notification.open} message={notification.message} severity={notification.severity} onClose={() => setNotification(n => ({ ...n, open: false }))} />
    </Box>
  )
}
